import { z } from "zod";

/**
 * MyKad scan through a third-party OCR (user, 2026-10-29): passports stay on
 * the in-browser MRZ reader; the MyKad front goes to `POST
 * /accreditations/scan/mykad`, which forwards it to the provider
 * (`MYKAD_SCAN_PROVIDER`, the API's env) and answers the fields. Nothing is
 * stored; the image is uploaded later with the record, as before.
 *
 * An accelerator, never a gate (like the flight lookup): provider off →
 * 200 `{ read: false, reason: "OFF" }`; a card it could not read → 200
 * `read: false`; a provider that did not answer → 503 SERVICE_UNAVAILABLE.
 * Every answer leads to "type the details" at worst.
 */

/**
 * The image as base64 (no `data:` prefix), JPEG or PNG. The API's body limit
 * is 1 MB, so the GMS shrinks the photo first; 900k characters ≈ 650 KB of
 * JPEG, far more than a card needs.
 */
export const MYKAD_SCAN_IMAGE_MAX_CHARS = 900_000;
/** Scans per login per window — the provider is paid per call, so admins count too. */
export const MYKAD_SCAN_MAX = 30;
export const MYKAD_SCAN_WINDOW_SECONDS = 600;

export const mykadScanBody = z.object({
	image_base64: z
		.string()
		.min(100)
		.max(MYKAD_SCAN_IMAGE_MAX_CHARS)
		.regex(/^[A-Za-z0-9+/]+={0,2}$/, "Base64 without a data: prefix"),
});
export type MykadScanBody = z.input<typeof mykadScanBody>;

/**
 *   OFF         no provider is configured — type the details
 *   NO_CARD     the provider found no Malaysian identity card in the photo
 *   UNREADABLE  it found one but could not read it (blur, glare, damage)
 */
export const MYKAD_SCAN_MISS_REASONS = ["OFF", "NO_CARD", "UNREADABLE"] as const;
export type MykadScanMissReason = (typeof MYKAD_SCAN_MISS_REASONS)[number];

/** What the provider noticed about the photo — shown as hints, never a refusal. */
export const MYKAD_SCAN_WARNINGS = ["covered", "photocopy", "photographed", "edited", "reflective", "blurry"] as const;
export type MykadScanWarning = (typeof MYKAD_SCAN_WARNINGS)[number];

export interface MykadScanFields {
	/** As read (`YYMMDD-PB-####` when well read); the GMS decodes and checks it (`icInfo`). */
	id_number: string | null;
	name: string | null;
	address: string | null;
	/** The provider's card type: MyKad, MyPR, MyKAS, MyKid, MyTentera, IKAD, POLIS. */
	card_type: string | null;
	warnings: MykadScanWarning[];
	/** The provider's confidence per field, 0..1; null when it gave none. */
	confidence: { id_number: number | null; name: number | null; address: number | null };
}

export type MykadScanResult = { read: true; reason: null; fields: MykadScanFields } | { read: false; reason: MykadScanMissReason; fields: null };
